import { ElMessage } from 'element-plus';

// 定义消息类型
export interface ChatMessage {
  id: number;
  fromUserId: number;
  fromUserName: string;
  fromUserAvatar: string;
  content: string;
  createTime: string;
}

export interface NotificationMessage {
  id: number;
  userId: number;
  fromUserId: number;
  fromUserName: string;
  fromUserAvatar: string;
  type: number; // 1-点赞帖子，2-点赞评论，3-评论帖子，4-回复评论，5-关注，6-收藏帖子
  targetType: number; // 1-帖子，2-评论，3-用户
  targetId: number;
  content: string | null;
  isRead: number;
  createTime: string;
}

export interface UnreadCountMessage {
  announcementCount: number;
  notificationCount: number;
  totalCount: number;
}

// 添加公告消息接口
export interface AnnouncementMessage {
  id: number;
  title: string;
  content: string;
  type: number; // 1-系统通知, 2-活动公告, 3-维护通知, 4-版本更新
  priority: number;
  publisherId: number;
  publishTime: number;
  sortOrder: number;
  isRead: number;
  readTime?: number;
  createTime: number;
}

class GlobalChatWebSocketService {
  private static instance: GlobalChatWebSocketService;
  private ws: WebSocket | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectInterval = 5000;
  private heartbeatInterval: number | null = null;
  
  private messageHandlers: ((message: ChatMessage) => void)[] = [];
  private notificationHandlers: ((notification: NotificationMessage) => void)[] = [];
  private connectionHandlers: ((connected: boolean) => void)[] = [];
  private unreadCountHandlers: ((unreadCount: UnreadCountMessage) => void)[] = [];
  // 添加公告处理器数组
  private announcementHandlers: ((announcement: AnnouncementMessage) => void)[] = [];

  public static getInstance(): GlobalChatWebSocketService {
    if (!GlobalChatWebSocketService.instance) {
      GlobalChatWebSocketService.instance = new GlobalChatWebSocketService();
    }
    return GlobalChatWebSocketService.instance;
  }

  connect(token: string) {
    if (this.ws && (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)) {
      console.log('WebSocket 已经连接或正在连接');
      return;
    }

    // 构建WebSocket连接URL
    // 在生产环境中，我们假设nginx会代理/ws路径到后端WebSocket服务
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const baseUrl = process.env.NODE_ENV === 'production' 
      ? `${protocol}//${window.location.host}` 
      : `${protocol}//localhost:8080`;
    
    const wsUrl = `${baseUrl}/ws/${token}`;
    
    console.log('尝试连接WebSocket:', wsUrl);
    
    try {
      this.ws = new WebSocket(wsUrl);
      
      this.ws.onopen = () => {
        console.log('✅ WebSocket连接已建立');
        this.reconnectAttempts = 0;
        this.startHeartbeat();
        this.connectionHandlers.forEach(handler => handler(true));
      };

      this.ws.onmessage = (event) => {
        console.log('WebSocket收到原始数据:', event.data);
        try {
          const data = event.data;
          
          // 心跳响应
          if (data === 'PONG') {
            return;
          }
          
          // 解析JSON消息
          const message = JSON.parse(data);
          this.handleMessage(message);
        } catch (error) {
          console.error('解析WebSocket消息失败:', error);
          console.log('原始消息:', event.data);
        }
      };

      this.ws.onclose = (event) => {
        console.log('❌ WebSocket连接已关闭', {
          code: event.code,
          reason: event.reason
        });
        this.stopHeartbeat();
        this.connectionHandlers.forEach(handler => handler(false));
        
        // 尝试重连
        if (this.reconnectAttempts < this.maxReconnectAttempts) {
          setTimeout(() => {
            this.reconnectAttempts++;
            console.log(`尝试重连 (${this.reconnectAttempts}/${this.maxReconnectAttempts})`);
            this.connect(token);
          }, this.reconnectInterval);
        } else {
          console.log('达到最大重连次数，停止重连');
          ElMessage.error('聊天服务连接失败，请刷新页面重试');
        }
      };

      this.ws.onerror = (error) => {
        console.error('⚠️ WebSocket发生错误:', error);
        ElMessage.error('聊天服务连接异常');
      };
    } catch (error) {
      console.error('创建WebSocket连接失败:', error);
      ElMessage.error('无法连接到聊天服务');
    }
  }

  disconnect() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
    
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
  }

  private handleMessage(message: any) {
    console.log('WebSocket收到消息:', message);
    console.log('当前注册的消息处理器数量:', this.messageHandlers.length);
    console.log('当前注册的通知处理器数量:', this.notificationHandlers.length);
    console.log('当前注册的连接处理器数量:', this.connectionHandlers.length);
    console.log('当前注册的未读数处理器数量:', this.unreadCountHandlers.length);
    console.log('当前注册的公告处理器数量:', this.announcementHandlers.length);
    
    switch (message.type) {
      case 'MESSAGE': {
        // 解析并处理接收到的消息
        const chatMessage: ChatMessage = message;
        console.log('处理 MESSAGE 类型消息:', chatMessage);
        if (this.messageHandlers.length > 0) {
          this.messageHandlers.forEach((handler, index) => {
            console.log(`执行第 ${index} 个消息处理器`);
            handler(chatMessage);
          });
        } else {
          console.warn('没有注册的消息处理器');
        }
        break;
      }
      case 'NOTIFICATION': {
        // 解析并处理通知消息
        const notificationMessage: NotificationMessage = message.data;
        console.log('处理 NOTIFICATION 类型消息:', notificationMessage);
        if (this.notificationHandlers.length > 0) {
          this.notificationHandlers.forEach((handler, index) => {
            console.log(`执行第 ${index} 个通知处理器`);
            handler(notificationMessage);
          });
        } else {
          console.warn('没有注册的通知处理器');
        }
        break;
      }
      case 'UNREAD_COUNT': {
        // 解析并处理未读数更新消息
        const unreadCountMessage: UnreadCountMessage = message.data;
        console.log('处理 UNREAD_COUNT 类型消息:', unreadCountMessage);
        if (this.unreadCountHandlers.length > 0) {
          this.unreadCountHandlers.forEach((handler, index) => {
            console.log(`执行第 ${index} 个未读数处理器`);
            handler(unreadCountMessage);
          });
        } else {
          console.warn('没有注册的未读数处理器');
        }
        break;
      }
      case 'ANNOUNCEMENT': {
        // 解析并处理公告消息
        const announcementMessage: AnnouncementMessage = message.data;
        console.log('处理 ANNOUNCEMENT 类型消息:', announcementMessage);
        if (this.announcementHandlers.length > 0) {
          this.announcementHandlers.forEach((handler, index) => {
            console.log(`执行第 ${index} 个公告处理器`);
            handler(announcementMessage);
          });
        } else {
          console.warn('没有注册的公告处理器');
        }
        break;
      }
      case 'NEW_MESSAGE': {
        // NEW_MESSAGE 类型的消息数据在 data 字段中，需要提取出来
        const messageData = message.data;
        if (messageData) {
          // 确保数据结构符合 ChatMessage 接口
          const chatMessage: ChatMessage = {
            id: messageData.id,
            fromUserId: messageData.fromUserId,
            fromUserName: messageData.fromUserName || '未知用户',
            fromUserAvatar: messageData.fromUserAvatar || '',
            content: messageData.content,
            createTime: messageData.createTime
          };
          
          console.log('处理 NEW_MESSAGE 类型消息:', chatMessage);
          if (this.messageHandlers.length > 0) {
            this.messageHandlers.forEach((handler, index) => {
              console.log(`执行第 ${index} 个 NEW_MESSAGE 处理器，传递消息:`, chatMessage);
              handler(chatMessage);
            });
          } else {
            console.warn('没有注册的消息处理器');
          }
        } else {
          console.warn('NEW_MESSAGE 消息缺少 data 字段:', message);
        }
        break;
      }
      default:
        console.log('收到未知类型的WebSocket消息:', message.type);
    }
  }

  private startHeartbeat() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
    }

    this.heartbeatInterval = window.setInterval(() => {
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        // 发送心跳
        this.ws.send('PING');
        console.log('发送心跳 PING');
      }
    }, 30000); // 每30秒发送一次心跳
  }

  private stopHeartbeat() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  addMessageHandler(handler: (message: ChatMessage) => void) {
    this.messageHandlers.push(handler);
  }

  removeMessageHandler(handler: (message: ChatMessage) => void) {
    const index = this.messageHandlers.indexOf(handler);
    if (index > -1) {
      this.messageHandlers.splice(index, 1);
    }
  }

  addNotificationHandler(handler: (notification: NotificationMessage) => void) {
    this.notificationHandlers.push(handler);
  }

  removeNotificationHandler(handler: (notification: NotificationMessage) => void) {
    const index = this.notificationHandlers.indexOf(handler);
    if (index > -1) {
      this.notificationHandlers.splice(index, 1);
    }
  }

  addUnreadCountHandler(handler: (unreadCount: UnreadCountMessage) => void) {
    this.unreadCountHandlers.push(handler);
  }

  removeUnreadCountHandler(handler: (unreadCount: UnreadCountMessage) => void) {
    const index = this.unreadCountHandlers.indexOf(handler);
    if (index > -1) {
      this.unreadCountHandlers.splice(index, 1);
    }
  }

  // 添加公告处理器
  addAnnouncementHandler(handler: (announcement: AnnouncementMessage) => void) {
    this.announcementHandlers.push(handler);
  }

  // 移除公告处理器
  removeAnnouncementHandler(handler: (announcement: AnnouncementMessage) => void) {
    const index = this.announcementHandlers.indexOf(handler);
    if (index > -1) {
      this.announcementHandlers.splice(index, 1);
    }
  }

  addConnectionHandler(handler: (connected: boolean) => void) {
    this.connectionHandlers.push(handler);
  }

  removeConnectionHandler(handler: (connected: boolean) => void) {
    const index = this.connectionHandlers.indexOf(handler);
    if (index > -1) {
      this.connectionHandlers.splice(index, 1);
    }
  }

  isConnected(): boolean {
    return this.ws !== null && this.ws.readyState === WebSocket.OPEN;
  }
}

export default GlobalChatWebSocketService.getInstance();